import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { BottleScene } from "@/poc/BottleScene";
import { HOME_CHAPTERS, CATEGORIES_UI, CHANNELS, CH7_STATS, MAP_STATES } from "@/home/content";
import { WorldRoutes } from "@/home/WorldRoutes";
import { IndiaDotMap } from "@/home/IndiaDotMap";
import { Ch9Section, EndFrameBand } from "@/home/SharedSections";

/*
 * Simplified homepage for prefers-reduced-motion and WebGL-unavailable
 * visitors. Every chapter's content remains fully readable — no pinned
 * choreography, no idle spin, real DOM text throughout.
 */

const SectionShell = ({ id, bg, children }) => (
  <section data-testid={`static-chapter-${id}`} className={`${bg} px-6 sm:px-[9vw] py-24 sm:py-32`}>
    {children}
  </section>
);

const Headline = ({ children }) => (
  <h2 className="hcc-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-[1.1] text-[#F7F1E7]">{children}</h2>
);

const Body = ({ children }) => (
  <p className="mt-6 text-sm sm:text-base font-light leading-relaxed text-[#F7F1E7]/70 max-w-2xl">{children}</p>
);

const Ctas = ({ ctas }) => (
  <div className="mt-8 flex flex-wrap gap-3">
    {ctas.map((c, i) => (
      <Link
        key={c.to + c.label}
        to={c.to}
        data-testid={`${c.testId}-static`}
        className={`inline-block px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold transition-all duration-300 ${
          i === 0
            ? "bg-[#F7F1E7] text-[#111111] hover:bg-[#C7A45D]"
            : "border border-[#C7A45D]/60 text-[#C7A45D] hover:bg-[#C7A45D] hover:text-[#111111]"
        }`}
      >
        {c.label}
      </Link>
    ))}
  </div>
);

export default function StaticHome({ heroType = "image", isMobile = false }) {
  const mouseRef = useRef({ x: 0, y: 0 });
  const progressRef = useRef(0);
  const [ch1, ch2, ch3, ch4, ch5, ch6, ch7, ch8] = HOME_CHAPTERS;

  return (
    <div data-testid="static-home" className="bg-[#111111] text-[#F7F1E7]">
      {/* Hero */}
      <section className="relative min-h-screen overflow-hidden flex items-end">
        <div className="absolute inset-0" aria-hidden="true">
          {heroType === "canvas" ? (
            <BottleScene progressRef={progressRef} mouseRef={mouseRef} isMobile={isMobile} staticMode />
          ) : (
            <img
              data-testid="static-fallback-image"
              src="/bottle-fallback.jpg"
              alt=""
              className="h-full w-full object-cover object-center"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}
        </div>
        <div className="hcc-vignette absolute inset-0 pointer-events-none" aria-hidden="true" />
        <div className="relative z-10 pb-[10vh] pl-6 sm:pl-[7vw] pr-6 max-w-2xl pt-40">
          <p className="mb-6 text-[10px] sm:text-[11px] tracking-[0.4em] text-[#C7A45D] font-medium">{ch1.label}</p>
          <h1 data-testid="static-hero-headline" className="hcc-serif hcc-text-shadow text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05]">
            {ch1.headline}
          </h1>
          <p className="mt-7 text-sm sm:text-base font-light leading-relaxed text-[#F7F1E7]/70 max-w-xl">{ch1.body}</p>
          <Ctas ctas={ch1.ctas} />
        </div>
      </section>

      {/* CH2 */}
      <SectionShell id={2} bg="bg-[#111111]">
        <Headline>{ch2.headline}</Headline>
        <Body>{ch2.body}</Body>
        <p className="hcc-serif mt-6 text-xl italic text-[#C7A45D]/90">{ch2.phrase}</p>
      </SectionShell>

      {/* CH3 categories */}
      <SectionShell id={3} bg="bg-[#1d0a10]">
        <Headline>{ch3.headline}</Headline>
        <div className="mt-10 max-w-2xl">
          {CATEGORIES_UI.map((c) => (
            <Link
              key={c.slug}
              to={`/portfolio?category=${c.slug}`}
              className="flex flex-wrap items-baseline justify-between gap-3 border-b border-white/10 py-3 hover:border-[#C7A45D]/60 transition-colors"
            >
              <span className="hcc-serif text-2xl text-[#F7F1E7]">{c.name}</span>
              <span className="text-[10px] tracking-[0.16em] uppercase text-[#F7F1E7]/50">{c.origins}</span>
            </Link>
          ))}
        </div>
      </SectionShell>

      {/* CH4 origins */}
      <SectionShell id={4} bg="bg-[#111111]">
        <Headline>{ch4.headline}</Headline>
        <Body>{ch4.body}</Body>
        <div className="mt-10 max-w-3xl">
          <WorldRoutes animated={false} />
        </div>
      </SectionShell>

      {/* CH5 market access */}
      <SectionShell id={5} bg="bg-[#140d10]">
        <Headline>{ch5.headline}</Headline>
        <Body>{ch5.body}</Body>
        <div className="mt-10 grid md:grid-cols-2 gap-10 items-start max-w-4xl">
          <div className="max-w-xs">
            <IndiaDotMap />
          </div>
          <div className="flex flex-wrap gap-2">
            {MAP_STATES.map((s) => (
              <span key={s} className="px-3.5 py-2 border border-white/15 text-[11px] uppercase tracking-[0.12em] text-[#F7F1E7]/70">
                {s}
              </span>
            ))}
          </div>
        </div>
        <Ctas ctas={ch5.ctas} />
      </SectionShell>

      {/* CH6 channels */}
      <SectionShell id={6} bg="bg-[#111111]">
        <Headline>{ch6.headline}</Headline>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-10 max-w-4xl">
          {CHANNELS.map((chn, i) => (
            <div key={chn} className="bg-[#101010] px-5 py-7">
              <span className="text-[10px] text-[#C7A45D] tracking-[0.25em]">{String(i + 1).padStart(2, "0")}</span>
              <p className="mt-2 text-sm tracking-[0.08em] uppercase text-[#F7F1E7]/85">{chn}</p>
            </div>
          ))}
        </div>
      </SectionShell>

      {/* CH7 infrastructure */}
      <SectionShell id={7} bg="bg-[#100e0c]">
        <Headline>{ch7.headline}</Headline>
        <div className="mt-8 space-y-4 max-w-2xl">
          {CH7_STATS.map((s) => (
            <div key={s.label} className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-white/10 pb-3.5">
              <span className="hcc-serif text-2xl sm:text-3xl text-[#C7A45D]">{s.value}</span>
              <span className="text-xs uppercase tracking-[0.14em] text-[#F7F1E7]/60">{s.label}</span>
            </div>
          ))}
        </div>
        <Body>{ch7.body}</Body>
      </SectionShell>

      {/* CH8 partnership */}
      <SectionShell id={8} bg="bg-[#23090f]">
        <Headline>{ch8.headline}</Headline>
        <Body>{ch8.body}</Body>
        <Ctas ctas={ch8.ctas} />
      </SectionShell>

      <Ch9Section />
      <EndFrameBand />
    </div>
  );
}
